import { cn } from "@/utils";
import { useDndContext, useDroppable } from "@dnd-kit/core";
import { Star } from "lucide-react";
import { useMemo } from "react";
import { classifyTransfer } from "./classifyTransfer";
import { asDraggableData, type DraggableData, type DroppableData } from "./ids";
import { transferDropTargetFavoritesActiveClassName } from "./transferDropTargetStyles";
import type { FavoritesResourceType } from "./types";

/** The favorites bucket each draggable kind stars into. */
function favoriteResourceTypeForDrag(
  draggable: DraggableData,
): FavoritesResourceType | null {
  switch (draggable.kind) {
    case "canvas-card":
      return "canvas";
    case "chat-session":
      return "chat-inbox";
    case "files":
      return "file-record";
    case "skill":
      return "skill";
    case "integration-connection":
      return "integration-connection";
    default:
      return null;
  }
}

/**
 * A consolidated Favorites drop target for the unified rail — unlike
 * [`RailFavoritesDropTarget`] (scoped to one resource type), this accepts ANY
 * favoritable row (canvas / chat / file) and stars it. The destination's
 * resource type is derived from the active drag, so a canvas stars as a canvas,
 * a chat via the inbox star, a file via its resource tag. The dispatcher's
 * `rail-favorites` branch handles the star exactly as before.
 */
export function RailAllFavoritesDropTarget({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const dropId = "rail-favorites:all";
  const { active, over } = useDndContext();

  const draggable = active ? asDraggableData(active.data.current) : null;
  const resourceType = draggable
    ? favoriteResourceTypeForDrag(draggable)
    : null;

  const droppableData = useMemo(
    (): DroppableData => ({
      surface: "rail-favorites",
      // Fall back to "canvas" when there's no favoritable drag — the value is
      // only consumed on drop, and a non-favoritable drag never lands here.
      destination: {
        kind: "favorites",
        resourceType: resourceType ?? "canvas",
      },
    }),
    [resourceType],
  );

  const droppable = useDroppable({ id: dropId, data: droppableData });

  const hasFavoritable =
    draggable !== null &&
    resourceType !== null &&
    classifyTransfer(draggable, { kind: "favorites", resourceType }).kind ===
      "favorite";

  const showHighlight = over?.id === dropId && hasFavoritable;

  return (
    <div
      ref={droppable.setNodeRef}
      className={cn(
        "relative min-h-0 rounded-xl transition-colors duration-150",
        showHighlight && transferDropTargetFavoritesActiveClassName,
        className,
      )}
    >
      {showHighlight ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-xl"
        >
          <Star className="size-7 fill-amber-500/20 text-amber-500/35" />
        </div>
      ) : null}
      {children}
    </div>
  );
}
